import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import dayjs from "dayjs";

import { GBSCError } from "@/models/GBSCError";
import {
  IAppointmentReport,
  IAppointmentReportResponse,
} from "@/models/ApiRequestResponse/report";
import { getAppointmentReportApi } from "@/utils/apis/doctor/report";

import { RootState } from "@/store/store";

export interface FilterState {
  clinicId: string;
  consultationType: string;
  status: string;
  patientType: string;
}

interface FetchReportPayload {
  startDate: string;
  endDate: string;
  filters: Partial<FilterState>;
}

export const fetchAppointmentReport = createAsyncThunk<
  IAppointmentReportResponse | GBSCError,
  FetchReportPayload
>("appointmentReport/fetch", async ({ startDate, endDate, filters }) => {
  const adjustedEndDate = dayjs(endDate).add(1, "day").format("YYYY-MM-DD");
  const response = await getAppointmentReportApi(
    startDate,
    adjustedEndDate,
    filters
  );
  return response;
});

const appointmentReportSlice = createSlice({
  name: "appointmentReport",
  initialState: {
    list: [] as IAppointmentReport[],
    count: 0,
    isLoading: true,
    error: null as string | null,
  },
  reducers: {
    resetAppointmentReport: (state) => {
      state.list = [];
      state.count = 0;
      state.isLoading = true;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointmentReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentReport.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.list = action.payload.result.list;
          state.count = action.payload.result.count;
        }
      })
      .addCase(fetchAppointmentReport.rejected, (state) => {
        state.isLoading = false;
        state.error = "Failed to fetch appointment report";
      });
  },
});

const selectAppointmentReport = (state: RootState) => state.appointmentReport;

export const appointmentReportSelector = createSelector(
  [selectAppointmentReport],
  (appointmentReport) => ({
    list: appointmentReport.list,
    count: appointmentReport.count,
    isLoading: appointmentReport.isLoading,
    error: appointmentReport.error,
  })
);

export const { resetAppointmentReport } = appointmentReportSlice.actions;

export default appointmentReportSlice.reducer;
